import { Button } from "@volca/ui/components/ui/button";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
} from "@volca/ui/components/ui/empty";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { emptyStateVariants } from "@volca/ui/lib/empty-state";
import {
  AgentRunningNotice,
  SectionHeading,
  StaleDataNotice,
  StatusBadge,
} from "@/accounts-payable/common";
import type { ApAgentRun, ApPurchaseDocument } from "@/accounts-payable/types";
import {
  AUTOMATED_RUN_BADGE,
  isAutomatedRun,
  isPendingReviewAction,
} from "@/accounts-payable/utils";
import { ActionGroup } from "./action-group";

/**
 * The agent's proposals for one document as one checklist in two groups: what
 * still needs review (open by default) and what's resolved (collapsed while
 * anything is pending, so the open work leads).
 */
export const AgentActionsSection = ({
  document,
  run,
  activeRun,
  loading,
  error,
  onViewAgentReview,
}: {
  document: Pick<ApPurchaseDocument, "id" | "status" | "document_type">;
  run: ApAgentRun | undefined;
  activeRun: ApAgentRun | undefined;
  /** Runs still loading — hold the empty state so it can't flash a false "none". */
  loading: boolean;
  /** Runs failed to load — say so rather than claim there are none. */
  error: boolean;
  onViewAgentReview?: (documentId: string) => void;
}) => {
  const kind = document.document_type ?? "invoice";
  const actions = run?.actions ?? [];
  const pending = actions.filter(isPendingReviewAction);
  const resolved = actions.filter((a) => !isPendingReviewAction(a));

  return (
    <Stack className="w-full" align="start" gap={4}>
      <Stack
        direction="row"
        justify="between"
        align="center"
        gap={4}
        className="w-full flex-wrap"
      >
        <Stack direction="row" gap={2} align="center" className="flex-wrap">
          <SectionHeading>Agent actions</SectionHeading>
          {run && isAutomatedRun(run) && (
            <StatusBadge meta={AUTOMATED_RUN_BADGE} />
          )}
        </Stack>
        {actions.length > 0 && onViewAgentReview && (
          <Button
            variant={pending.length > 0 ? "secondary" : "outline"}
            onClick={() => onViewAgentReview(document.id)}
          >
            {pending.length > 0 ? "Review & approve" : "View all"}
          </Button>
        )}
      </Stack>

      {/* See the review page: the notice stands alongside a superseded run's
          proposals rather than being hidden by them. */}
      {activeRun ? <AgentRunningNotice /> : <StaleDataNotice run={run} />}

      {error && actions.length === 0 && (
        <Text tone="muted">Couldn’t load agent actions. Please try again.</Text>
      )}

      {actions.length === 0 && !activeRun && !loading && !error && (
        <Empty className={emptyStateVariants({ layout: "inline" })}>
          <EmptyHeader>
            <EmptyDescription>
              {document.status === "matched"
                ? `No agent actions for this ${kind} yet.`
                : `The agent runs once the ${kind} is parsed and matched.`}
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      )}

      <ActionGroup label="Needs review" actions={pending} defaultOpen />

      <ActionGroup
        label="Resolved"
        actions={resolved}
        defaultOpen={pending.length === 0}
      />
    </Stack>
  );
};
